import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { auditService } from '@/services/auditService';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

export type TransferStatus =
  | 'solicitado'
  | 'aprovado'
  | 'em_transito'
  | 'recebido'
  | 'cancelado';

export interface StockTransferItem {
  id: string;
  transfer_id: string;
  product_id: string;
  qty_requested: number;
  qty_sent: number | null;
  qty_received: number | null;
  unit_cost: number | null;
  divergence_notes: string | null;
  company_id: string;
  product?: {
    id: string;
    name: string;
    sku: string | null;
  };
}

export interface StockTransfer {
  id: string;
  document_number: string;
  company_id_from: string;
  company_id_to: string;
  deposit_id_from: string;
  deposit_id_to: string;
  status: TransferStatus;
  requested_by: string;
  requested_at: string;
  approved_by: string | null;
  approved_at: string | null;
  dispatched_by: string | null;
  dispatched_at: string | null;
  dispatch_signature_data: string | null;
  dispatch_signature_name: string | null;
  dispatched_by_name: string | null;
  delivered_by: string | null;
  received_by: string | null;
  received_at: string | null;
  receive_signature_data: string | null;
  receive_signature_name: string | null;
  received_by_name: string | null;
  notes: string | null;
  has_divergence: boolean;
  company_id: string;
  created_at: string;
  company_from?: { id: string; name: string };
  company_to?: { id: string; name: string };
  deposit_from?: { id: string; name: string };
  deposit_to?: { id: string; name: string };
  items?: StockTransferItem[];
}

export interface CreateTransferInput {
  company_id_from: string;
  company_id_to: string;
  deposit_id_from: string;
  deposit_id_to: string;
  notes?: string;
  items: Array<{
    product_id: string;
    qty_requested: number;
  }>;
}

export interface UpdateTransferStatusInput {
  id: string;
  status: TransferStatus;
  extra?: {
    approved_by?: string;
    approved_at?: string;
    dispatched_by?: string;
    dispatched_at?: string;
    dispatch_signature_data?: string;
    dispatch_signature_name?: string;
    dispatched_by_name?: string;
    received_by?: string;
    received_at?: string;
    receive_signature_data?: string;
    receive_signature_name?: string;
    received_by_name?: string;
  };
  items?: Array<{
    id: string;
    qty_sent?: number;
    qty_received?: number;
    unit_cost?: number;
    divergence_notes?: string;
  }>;
}

// =============================================================================
// useTransfersList — lista transferências da empresa ativa (from ou to)
// =============================================================================

export function useTransfersList(filters?: {
  status?: TransferStatus;
  dateFrom?: string;
  dateTo?: string;
  /** Quando true, não dispara a query (ex.: visão consolidada com outra fonte). */
  skip?: boolean;
}) {
  const { company } = useAuth();
  const companyId = company?.id ?? null;
  const status = filters?.status ?? null;
  const dateFrom = filters?.dateFrom ?? null;
  const dateTo = filters?.dateTo ?? null;
  const skip = Boolean(filters?.skip);

  return useQuery({
    queryKey: ['stock-transfers', companyId, status, dateFrom, dateTo, skip],
    queryFn: async () => {
      let query = supabase
        .from('stock_transfers')
        .select(`
          *,
          company_from:companies!company_id_from(id, name),
          company_to:companies!company_id_to(id, name),
          deposit_from:deposits!deposit_id_from(id, name),
          deposit_to:deposits!deposit_id_to(id, name)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as StockTransfer[];
    },
    enabled: !skip && !!companyId,
    staleTime: 0,
  });
}

// =============================================================================
// useTransferById — detalhe com itens
// Padrão Sprint Abril 2026: staleTime 0 + refetchOnMount always
// =============================================================================

export function useTransferById(id: string | null) {
  return useQuery({
    queryKey: ['stock-transfer-detail', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('stock_transfers')
        .select(`
          *,
          company_from:companies!company_id_from(id, name),
          company_to:companies!company_id_to(id, name),
          deposit_from:deposits!deposit_id_from(id, name),
          deposit_to:deposits!deposit_id_to(id, name),
          items:stock_transfer_items(
            *,
            product:products(id, name, sku)
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as StockTransfer | null;
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// =============================================================================
// useCreateTransfer — cria nova solicitação de transferência
// =============================================================================

export function useCreateTransfer() {
  const queryClient = useQueryClient();
  const { company, user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateTransferInput) => {
      if (!company?.id || !user?.id) throw new Error('Usuário não autenticado');

      // Gera número do documento via função SQL
      const { data: docNumber, error: numError } = await supabase
        .rpc('generate_transfer_number');
      if (numError) throw numError;

      // Cria o cabeçalho
      const { data: transfer, error: transferError } = await supabase
        .from('stock_transfers')
        .insert({
          document_number: docNumber as string,
          company_id_from: input.company_id_from,
          company_id_to: input.company_id_to,
          deposit_id_from: input.deposit_id_from,
          deposit_id_to: input.deposit_id_to,
          status: 'solicitado',
          requested_by: user.id,
          requested_at: new Date().toISOString(),
          notes: input.notes ?? null,
          company_id: company.id,
        })
        .select('id, document_number')
        .single();
      if (transferError) throw transferError;

      // Cria os itens
      const itemsToInsert = input.items.map((item) => ({
        transfer_id: transfer.id,
        product_id: item.product_id,
        qty_requested: item.qty_requested,
        company_id: input.company_id_from,
      }));

      const { error: itemsError } = await supabase
        .from('stock_transfer_items')
        .insert(itemsToInsert);
      if (itemsError) throw itemsError;

      return transfer;
    },
    onSuccess: async (transfer) => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-detail', transfer.id] });

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'ESTOQUE',
        entityType: 'transferência',
        entityId: transfer.id,
        description: `Transferência ${transfer.document_number} solicitada`,
        details: { document_number: transfer.document_number, status: 'solicitado' },
      });

      toast.success(`Transferência ${transfer.document_number} criada com sucesso!`);
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar transferência: ${error.message}`);
    },
  });
}

// =============================================================================
// useUpdateTransferStatus — muda status + dados opcionais de despacho/recebimento
// =============================================================================

export function useUpdateTransferStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: UpdateTransferStatusInput) => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // Busca status anterior para o audit log
      const { data: current } = await supabase
        .from('stock_transfers')
        .select('status, document_number')
        .eq('id', input.id)
        .single();

      // Monta payload de atualização
      const updatePayload: Record<string, unknown> = {
        status: input.status,
        ...(input.extra ?? {}),
      };

      const { error: updateError } = await supabase
        .from('stock_transfers')
        .update(updatePayload)
        .eq('id', input.id);
      if (updateError) throw updateError;

      // Atualiza itens se fornecidos (despacho ou recebimento)
      if (input.items && input.items.length > 0) {
        for (const item of input.items) {
          const { id: itemId, ...itemData } = item;
          const { error: itemError } = await supabase
            .from('stock_transfer_items')
            .update(itemData)
            .eq('id', itemId);
          if (itemError) throw itemError;
        }
      }

      return { id: input.id, previousStatus: current?.status, document_number: current?.document_number };
    },
    onSuccess: async ({ id, previousStatus, document_number }, input) => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-detail', id] });

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'ESTOQUE',
        entityType: 'transferência',
        entityId: id,
        description: `Transferência ${document_number} — status: ${previousStatus} → ${input.status}`,
        details: {
          transfer_id: id,
          document_number,
          status_anterior: previousStatus,
          status_novo: input.status,
          user_id: user?.id,
          timestamp: new Date().toISOString(),
        },
      });

      const statusLabels: Record<TransferStatus, string> = {
        solicitado: 'Solicitada',
        aprovado: 'Aprovada',
        em_transito: 'Em Trânsito',
        recebido: 'Recebida',
        cancelado: 'Cancelada',
      };

      toast.success(`Transferência ${statusLabels[input.status] ?? input.status}!`);
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar transferência: ${error.message}`);
    },
  });
}
